import { useEffect } from "react";

type AsyncEffectCallback = () => Promise<void | (() => void)>;

/**
 * Custom hook that allows using async/await inside useEffect
 * Handles cleanup and prevents memory leaks when component unmounts
 *
 * @param asyncCallback - The async function to execute
 * @param deps - Dependency array, same as useEffect
 */
export const useAsyncEffect = (asyncCallback: AsyncEffectCallback, deps?: React.DependencyList) => {
  useEffect(() => {
    let isCancelled = false;
    let cleanup: (() => void) | void;

    const executeAsync = async () => {
      try {
        if (!isCancelled) {
          cleanup = await asyncCallback();
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Async effect error:", error);
          window.Honeybadger?.notify("Async effect error", {
            params: {
              error: error instanceof Error ? error.message : String(error),
              error_object: error instanceof Error ? error.toString() : String(error),
              stack_trace: error instanceof Error ? error.stack : null,
              hook: "useAsyncEffect"
            }
          });
        }
      }
    };

    executeAsync();

    return () => {
      isCancelled = true;
      if (cleanup && typeof cleanup === "function") {
        cleanup();
      }
    };
  }, deps);
};
